﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, Select,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, Tooltip, InlineGrid,
} from '@shopify/polaris';
import { HeartIcon, CashDollarIcon, EmailIcon } from '@shopify/polaris-icons';
import { useWinback } from '../../hooks/useWinback';
import type { CustomerWinbackCampaign } from '../../api/winback';
import { StatCard } from '../shared/StatCard';
import { IconBadge } from '../shared/IconBadge';

interface DetailProps {
  campaign: CustomerWinbackCampaign;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function CampaignDetail({ campaign, onBack, onDelete }: DetailProps) {
  return (
    <Page fullWidth
      title={`Win-Back Campaign (lapsed ${campaign.lapsed_after_days}+ days)`}
      backAction={{ content: 'All Campaigns', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(campaign.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">Customers analysed</Text>
                <Text as="p" variant="heading2xl" fontWeight="bold">{campaign.customers_analyzed}</Text>
              </BlockStack>
            </Card>

            {campaign.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{campaign.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Win-Back Recommendations</Text>
                {campaign.ai_recommendations.length === 0 ? (
                  <Text as="p" tone="subdued">No recommendations generated.</Text>
                ) : (
                  <BlockStack gap="300">
                    {campaign.ai_recommendations.map((r, i) => (
                      <Card key={i} background="bg-surface-secondary">
                        <BlockStack gap="150">
                          <InlineStack gap="200" blockAlign="center" align="space-between">
                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                              <IconBadge icon={HeartIcon} color="pink" />
                              <Text as="h3" variant="headingSm">{r.customer_name}</Text>
                            </InlineStack>
                            <Tooltip content="Copy email body">
                              <Button
                                size="micro"
                                variant="plain"
                                onClick={() => navigator.clipboard.writeText(r.email_body)}
                              >
                                Copy email
                              </Button>
                            </Tooltip>
                          </InlineStack>
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            Offer: {r.suggested_offer}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            Subject: {r.email_subject}
                          </Text>
                          <Text as="p" variant="bodyMd">{r.email_body}</Text>
                          <Text as="p" variant="bodySm" tone="subdued">{r.rationale}</Text>
                          <Text as="p" variant="bodySm" tone="success">
                            Expected impact: {r.expected_impact}
                          </Text>
                        </BlockStack>
                      </Card>
                    ))}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Lapsed Customer Candidates</Text>
                {campaign.candidates.length === 0 ? (
                  <Text as="p" tone="subdued">No candidates found.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric']}
                    headings={['Customer', 'Email', 'Lifetime Spend', 'Orders', 'Days Since Last Order']}
                    rows={campaign.candidates.map((c) => [
                      c.name,
                      c.email ?? '—',
                      `$${c.total_spent.toLocaleString()}`,
                      String(c.order_count),
                      String(c.days_since_last_order),
                    ])}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const LAPSED_OPTIONS = [
  { label: '30+ days quiet', value: '30' },
  { label: '60+ days quiet', value: '60' },
  { label: '90+ days quiet', value: '90' },
  { label: '180+ days quiet', value: '180' },
];

export function WinbackPanel() {
  const {
    campaigns, activeCampaign, loading, generating, error,
    total, page, lastPage, fetchCampaigns, generate, deleteCampaign, setActiveCampaign, setError,
  } = useWinback();

  const [lapsedAfterDays, setLapsedAfterDays] = useState('60');

  useEffect(() => {
    fetchCampaigns(1);
  }, []);

  if (activeCampaign) {
    return (
      <CampaignDetail
        campaign={activeCampaign}
        onBack={() => setActiveCampaign(null)}
        onDelete={deleteCampaign}
      />
    );
  }

  const latest = campaigns[0];
  const latestLifetimeSpend = latest ? latest.candidates.reduce((sum, c) => sum + c.total_spent, 0) : 0;

  return (
    <Page fullWidth
      title="Customer Win-Back Campaigns"
      subtitle="Finds high-value repeat customers who've gone quiet and drafts personalised win-back offers and emails"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {latest && (
            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
              <StatCard label="Lapsed Customers Found" value={String(latest.candidates.length)} icon={HeartIcon} iconColor="pink" />
              <StatCard
                label="Lifetime Spend at Stake"
                value={`$${latestLifetimeSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                icon={CashDollarIcon}
                iconColor="green"
              />
              <StatCard label="Win-Back Campaigns Run" value={String(total)} icon={EmailIcon} iconColor="blue" />
            </InlineGrid>
          )}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Campaign</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Analyses up to a year of order history to find repeat customers who haven't ordered recently,
                then drafts a personalised offer and email for each.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="200px">
                  <Select label="Lapsed threshold" options={LAPSED_OPTIONS} value={lapsedAfterDays} onChange={setLapsedAfterDays} />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={() => generate({ lapsed_after_days: Number(lapsedAfterDays) })}
                >
                  Generate Campaign
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Past Campaigns ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : campaigns.length === 0 ? (
                <EmptyState
                  heading="No win-back campaigns yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Campaign', onAction: () => generate({ lapsed_after_days: Number(lapsedAfterDays) }) }}
                >
                  <p>Generate a campaign to find lapsed customers worth winning back.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                  headings={['Lapsed Threshold', 'Customers Analysed', 'Recommendations', 'Actions']}
                  rows={campaigns.map((c) => [
                    <Button variant="plain" onClick={() => setActiveCampaign(c)}>{`${c.lapsed_after_days}+ days`}</Button>,
                    String(c.customers_analyzed),
                    String(c.ai_recommendations.length),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteCampaign(c.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchCampaigns(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchCampaigns(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
